import { GroupingStrategy } from "@/api/admin/useAutoGroupReservations";
import { AvailableDateResponseDto } from "@/api/generated/generatedTypes";
import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group as MantineGroup,
  Modal,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowsShuffle,
  IconFilter,
  IconInfoCircle,
  IconQuestionMark,
} from "@tabler/icons-react";
import { useState } from "react";
import { StagingGroupCard } from "./StagingGroupCard/StagingGroupCard";
import { Location, StagingGroup } from "./types";

interface StagingSectionProps {
  stagingGroups: StagingGroup[];
  availableDates: AvailableDateResponseDto[];
  locations: Location[];
  selectedStagingGroup: StagingGroup | null;
  onSelectStagingGroup: (group: StagingGroup | null) => void;
  onAddGroup: () => void;
  onUpdateDate: (groupId: string, date: string) => void;
  onUpdateLocation: (groupId: string, locationId: number) => void;
  onRemoveRequest: (groupId: string, requestId: number) => void;
  onSave: (groupId: string) => void;
  onCreate: (groupId: string) => void;
  onDelete: (groupId: string) => void;
  onAutoGroup: (strategy: GroupingStrategy) => void;
  isAutoGrouping: boolean;
}

export const StagingSection = ({
  stagingGroups,
  availableDates,
  locations,
  selectedStagingGroup,
  onSelectStagingGroup,
  onAddGroup,
  onUpdateDate,
  onUpdateLocation,
  onRemoveRequest,
  onSave,
  onCreate,
  onDelete,
  onAutoGroup,
  isAutoGrouping,
}: StagingSectionProps) => {
  const [strategy, setStrategy] = useState<GroupingStrategy>(GroupingStrategy.STRICT);
  const [helpOpened, { open: openHelp, close: closeHelp }] = useDisclosure(false);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <MantineGroup justify="space-between" mb="md">
        <Title order={2} c="black">
          그룹 편성
        </Title>
        <MantineGroup gap="sm">
          <SegmentedControl
            value={strategy}
            onChange={(value) => setStrategy(value as GroupingStrategy)}
            data={[
              { label: "엄격한 그루핑", value: GroupingStrategy.STRICT },
              { label: "최적 그루핑", value: GroupingStrategy.BEST_EFFORT },
            ]}
            size="sm"
          />
          <ActionIcon variant="default" color="gray" size="lg" onClick={openHelp}>
            <IconQuestionMark size={18} />
          </ActionIcon>
          <Button
            onClick={() => onAutoGroup(strategy)}
            loading={isAutoGrouping}
            variant="filled"
            color="blue"
          >
            자동 그룹 생성
          </Button>
        </MantineGroup>
      </MantineGroup>

      <Modal
        opened={helpOpened}
        onClose={closeHelp}
        title={
          <Text fw={700} c="black">
            자동 그루핑 알고리즘 안내
          </Text>
        }
        size="lg"
        centered
        styles={{ body: { color: "black" } }}
      >
        <Stack gap="lg">
          {/* STRICT */}
          <div>
            <MantineGroup gap="xs" mb="xs">
              <ThemeIcon variant="light" color="blue" size="md">
                <IconFilter size={16} />
              </ThemeIcon>
              <Text fw={700} size="lg">
                엄격한 그루핑
              </Text>
              <Badge color="blue" variant="light" size="sm">
                STRICT
              </Badge>
            </MantineGroup>

            <Text size="sm" c="dimmed" mb="sm">
              조건이 정확히 맞는 사람들만 그루핑합니다. 맞지 않는 사람은 관리자가 직접 배치합니다.
            </Text>

            <div
              style={{
                background: "#f8f9fa",
                borderRadius: 8,
                padding: "12px 16px",
                fontFamily: "monospace",
                fontSize: 13,
                lineHeight: 1.8,
              }}
            >
              <Text size="xs" fw={600} mb={4}>
                처리 순서:
              </Text>
              <Text size="xs">1. 날짜/시간이 같은 사람들끼리 묶음</Text>
              <Text size="xs">2. 그 안에서 장소가 겹치는 사람들끼리 묶음</Text>
              <Text size="xs">3. 나이 차이 8살 이내로 분리</Text>
              <Text size="xs">4. 남녀 비율 균등 배치 (3:3 목표)</Text>
              <Text size="xs">5. 4~6명 단위로 그룹 생성</Text>

              <Divider my="xs" />

              <Text size="xs" fw={600} mb={4}>
                예시:
              </Text>
              <Text size="xs">예약 10명 중 8명이 같은 날짜/장소/나이대</Text>
              <Text size="xs" c="teal">
                → 8명: 그룹 2개 생성 (4명 + 4명)
              </Text>
              <Text size="xs" c="red">
                → 2명: 조건 불일치 → 그루핑 안 됨 (관리자 직접 배치)
              </Text>
            </div>
          </div>

          <Divider />

          {/* BEST EFFORT */}
          <div>
            <MantineGroup gap="xs" mb="xs">
              <ThemeIcon variant="light" color="violet" size="md">
                <IconArrowsShuffle size={16} />
              </ThemeIcon>
              <Text fw={700} size="lg">
                최적 그루핑
              </Text>
              <Badge color="violet" variant="light" size="sm">
                BEST EFFORT
              </Badge>
            </MantineGroup>

            <Text size="sm" c="dimmed" mb="sm">
              점수 기반으로 최대한 모든 사람을 그루핑합니다. 완벽하지 않아도 가능한 조합을 찾습니다.
            </Text>

            <div
              style={{
                background: "#f8f9fa",
                borderRadius: 8,
                padding: "12px 16px",
                fontFamily: "monospace",
                fontSize: 13,
                lineHeight: 1.8,
              }}
            >
              <Text size="xs" fw={600} mb={4}>
                점수 산정 기준:
              </Text>
              <Text size="xs">• 그룹 크기 (4~6명 최적)</Text>
              <Text size="xs">• 성별 비율 (남녀 균등 3:3이 최고 점수)</Text>
              <Text size="xs">• 나이 차이 (적을수록 높은 점수)</Text>
              <Text size="xs">• 시간대 겹침 (공통 시간 많을수록 유리)</Text>
              <Text size="xs">• 장소 겹침 (공통 장소 많을수록 유리)</Text>
              <Text size="xs">• 직업 다양성 (다양할수록 높은 점수)</Text>

              <Divider my="xs" />

              <Text size="xs" fw={600} mb={4}>
                예시:
              </Text>
              <Text size="xs">예약 10명이 조건이 제각각이어도</Text>
              <Text size="xs" c="teal">
                → 10명: 가장 높은 점수 조합으로 그룹 2개 생성
              </Text>
              <Text size="xs" c="dimmed">
                → 완벽한 매칭은 아니지만 아무도 남지 않음
              </Text>
            </div>
          </div>

          <Divider />

          <MantineGroup gap="xs">
            <ThemeIcon variant="light" color="gray" size="sm">
              <IconInfoCircle size={14} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">
              두 알고리즘 모두 실행 후 결과는 스테이징에 추가됩니다. 확정 전에 수동으로 조정할 수
              있습니다.
            </Text>
          </MantineGroup>
        </Stack>
      </Modal>
      <ScrollArea style={{ flex: 1 }}>
        <Stack gap="md">
          {stagingGroups.map((group) => (
            <StagingGroupCard
              key={group.id}
              stagingGroup={group}
              availableDates={availableDates}
              locations={locations}
              isSelected={selectedStagingGroup?.id === group.id}
              onSelect={() => onSelectStagingGroup(group)}
              onUpdateDate={(date) => onUpdateDate(group.id, date)}
              onUpdateLocation={(locationId) => onUpdateLocation(group.id, locationId)}
              onRemoveRequest={(requestId) => onRemoveRequest(group.id, requestId)}
              onSave={() => onSave(group.id)}
              onCreate={() => onCreate(group.id)}
              onDelete={() => onDelete(group.id)}
            />
          ))}
          <Button onClick={onAddGroup} variant="outline">
            그룹 추가
          </Button>
        </Stack>
      </ScrollArea>
    </div>
  );
};
